import {
  ReactElement,
  ChangeEvent,
  useState,
  useEffect,
  useContext,
} from "react";
import {
  RiverTextInput,
  RiverDialog,
  IValidationErrors,
  useValidation,
  useNotification,
} from "@river/common-ui";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { IContractNote } from "@river/interfaces";
import styles from "./customer-contract-notes-add-edit-dialog.module.scss";
import { customerService } from "../../../../services";
import { CustomerContext } from "../../..";
import { ContractContext } from "../../contract.context";

interface ICustomerContractNotesAddEditDialogProps {
  open: boolean;
  noteToEdit: IContractNote | null;
  onClose: (success: boolean) => void;
}

export const CustomerContractNotesAddEditDialog = ({
  open,
  noteToEdit,
  onClose,
}: ICustomerContractNotesAddEditDialogProps): ReactElement => {
  const { customer } = useContext(CustomerContext)!;
  const [note, setNote] = useState<IContractNote | null>(noteToEdit || null);
  const [validationErrors, setValidationErrors] = useState<IValidationErrors>({
    fields: {},
    list: [],
  });
  const notify = useNotification();
  const { contract } = useContext(ContractContext)!;
  const { validateFields, validateProperty } = useValidation();

  const resetValidationErrors = (): void =>
    setValidationErrors({ fields: {}, list: [] });

  const resetDialogState = (): void => {
    resetValidationErrors();
    setNote(null);
  };

  const closeDialog = (success: boolean): void => {
    resetDialogState();
    onClose(success);
  };

  const onPropertyChange = async (
    event: ChangeEvent<{ name?: string; value: unknown }>
  ): Promise<void> => {
    let newState: any = {
      ...note,
      [event.target.name!]: event.target.value,
    };

    setNote(newState);
    const newErrors: IValidationErrors = await validateProperty(
      newState,
      event.target.name!,
      validationErrors
    );
    setValidationErrors(newErrors);
  };

  const validateNote = async (note: any): Promise<IValidationErrors> => {
    const errors = await validateFields(note);
    setValidationErrors(errors);
    return errors;
  };

  const addNote = async (): Promise<void> => {
    const errors: IValidationErrors = await validateNote(note);
    if (errors.list.length > 0) {
      return;
    }

    try {
      if (customer?._id && contract?._id) {
        if (note?._id) {
          await customerService.updateContractNote(
            customer._id,
            contract._id,
            note?._id!,
            note
          );
        } else {
          await customerService.createContractNote(
            customer._id,
            contract._id,
            note!
          );
        }

        closeDialog(true);
      }
    } catch (message) {
      notify.error({ message });
    }
  };

  const renderDescriptionField = (): ReactElement => (
    <div className={styles.fieldContainer}>
      <RiverTextInput
        id="note"
        label="Note..."
        value={note?.note || ""}
        onChangeEvent={(event) => onPropertyChange(event)}
        validationErrors={validationErrors?.fields["note"]}
        inputProps={{ multiline: true, minRows: 5 }}
        fullWidth
      />
      {validationErrors?.fields["note"]?.errors?.[0] && (
        <span className={styles.errorMessage}>
          {validationErrors.fields["note"].errors[0].message}
        </span>
      )}
    </div>
  );

  useEffect(() => {
    if (noteToEdit) {
      setNote(noteToEdit);
    }
  }, [noteToEdit]);

  return (
    <RiverDialog
      titleProps={{
        title: `${noteToEdit ? "Edit Note" : "Add a New Note"}`,
        icon: noteToEdit ? EditIcon : AddIcon,
      }}
      open={open}
      onClose={() => {
        closeDialog(false);
      }}
      actionButtonText={noteToEdit ? "Save" : "Add"}
      disabledActionButton={!note?.note}
      showActionsDivider={false}
      onSubmit={addNote}
    >
      {renderDescriptionField()}
    </RiverDialog>
  );
};
